import React from 'react';
import { useTranslation } from 'react-i18next';
import { rowActivation } from './rowActivation';
import { AttributePill, SystemChip } from './pills';

/**
 * DealCardRow (phone) + DealGridRow (desktop table) — UXeng's Phase-2 Pipeline spec
 * (fleet-reports/UXeng-phase2-per-screen.md §1). Comp columns: Deal · Stage · "Last activity" (was
 * "Health" — GTM's ruling: observation, not a fabricated verdict) · Value · Owner. Phone card: title
 * Deal, meta-right Value, meta-line Last activity; pills = Stage (editable AttributePill -> bottom
 * sheet) + Owner (read-only SystemChip). Two pills, one spare of the 3-pill budget.
 *
 * Stage changes NEVER drag on phone — AttributePill -> bottom sheet -> pick, identical on both tiers.
 */
export interface DealRow {
  id: string;
  name: string;
  value: number | null;
  stageId: string | null;
  stageName: string | null;
  ownerId: string | null;
  ownerName: string | null;
  lastCustomerActivityAt: string | null;
}

export interface DealRowCallbacks {
  selected?: boolean;
  onOpenDeal: (deal: DealRow) => void;
  onEditStage: (deal: DealRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => void;
}

function formatValue(v: number | null): string {
  return v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/**
 * "14d" (no "ago" — the column header supplies the frame), "today"/"Nh" under a day, full timestamp
 * in `title`. `null` renders an em-dash, NEVER "0d" — null is the default state of every new record
 * (Deal.lastCustomerActivityAt has no default and nothing writes it yet), and "0d" would misread as
 * "activity today" on a row that has never had any.
 */
export function lastActivityLabel(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return 'today';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Neutral unless the account has DECLARED a threshold (Class-A — PM's ruling, plan tip 7ac7cef0):
 *  no system default, and never rose (rose is reserved for the conversations pane's 24h window). */
export function lastActivityTone(iso: string | null, staleAfterDays: number | null): 'neutral' | 'amber' {
  if (!iso || staleAfterDays == null) return 'neutral';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days > staleAfterDays ? 'amber' : 'neutral';
}

export const LastActivity: React.FC<{ iso: string | null; staleAfterDays: number | null; className?: string }> = ({ iso, staleAfterDays, className }) => {
  const tone = lastActivityTone(iso, staleAfterDays);
  return (
    <span data-testid="deal-last-activity" className={`${className ?? ''} ${tone === 'amber' ? 'text-warning' : 'text-text-muted'}`} dir="ltr" title={iso ?? undefined}>
      {lastActivityLabel(iso)}
    </span>
  );
};

export const DealCardRow: React.FC<{ deal: DealRow; staleAfterDays: number | null } & DealRowCallbacks> = ({ deal, staleAfterDays, selected, onOpenDeal, onEditStage }) => {
  const { t } = useTranslation('common');
  return (
    <div {...rowActivation(() => onOpenDeal(deal))} onClick={() => onOpenDeal(deal)}
      className={`p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-strong truncate">{deal.name || '—'}</span>
        <span className="ms-auto text-[11px] text-text-muted shrink-0" dir="ltr">{formatValue(deal.value)}</span>
      </div>
      <LastActivity iso={deal.lastCustomerActivityAt} staleAfterDays={staleAfterDays} className="text-xs" />
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <AttributePill value={deal.stageName} label={t('pipelineGrid.columns.stage')} emptyText={t('customers.detail.setField', { field: t('pipelineGrid.columns.stage') })}
          onOpen={(a) => onEditStage(deal, a, 'sheet')} />
        {deal.ownerName && <SystemChip label={deal.ownerName} tone="muted" />}
      </div>
    </div>
  );
};

export const DealGridRow: React.FC<{ deal: DealRow; staleAfterDays: number | null } & DealRowCallbacks> = ({ deal, staleAfterDays, selected, onOpenDeal, onEditStage }) => {
  const { t } = useTranslation('common');
  return (
    <tr tabIndex={0} onKeyDown={rowActivation(() => onOpenDeal(deal)).onKeyDown} className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenDeal(deal)}>
      <td className="sticky z-10 bg-surface p-cell-y px-cell-x [inset-inline-start:0]">
        <span className="text-sm font-semibold text-text-strong truncate">{deal.name || '—'}</span>
      </td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <AttributePill value={deal.stageName} label={t('pipelineGrid.columns.stage')} emptyText={t('customers.detail.setField', { field: t('pipelineGrid.columns.stage') })}
          onOpen={(a) => { onEditStage(deal, a, 'popover'); }} />
      </td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <LastActivity iso={deal.lastCustomerActivityAt} staleAfterDays={staleAfterDays} className="text-sm" />
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-strong whitespace-nowrap" dir="ltr">{formatValue(deal.value)}</td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap truncate">{deal.ownerName || '—'}</td>
    </tr>
  );
};
